import type { DayKey, DayHours, PickupInfo } from '@/types'

/** The delivery_method value a self pickup order carries; mirrors OrderService::SELF_PICKUP. */
export const SELF_PICKUP = 'SELF PICKUP'

export const DAYS: { key: DayKey; short: string; long: string }[] = [
  { key: 'mon', short: 'Mon', long: 'Monday' },
  { key: 'tue', short: 'Tue', long: 'Tuesday' },
  { key: 'wed', short: 'Wed', long: 'Wednesday' },
  { key: 'thu', short: 'Thu', long: 'Thursday' },
  { key: 'fri', short: 'Fri', long: 'Friday' },
  { key: 'sat', short: 'Sat', long: 'Saturday' },
  { key: 'sun', short: 'Sun', long: 'Sunday' },
]

/** Hours a given open day actually keeps: its override, else the defaults, else nothing. */
export function effectiveHours(pickup: PickupInfo, day: DayKey): DayHours | null {
  const override = pickup.day_hours?.[day]
  if (override?.from && override?.to) return override
  if (pickup.open && pickup.close) return { from: pickup.open, to: pickup.close }
  return null
}

export interface HoursRow {
  /** "Mon - Thu, Sun" */
  days: string
  /** "09:00 - 17:00", or "" when no hours are set for those days. */
  hours: string
}

/**
 * The pickup hours table: open days grouped by identical hours, one row per
 * group, contiguous days collapsed into a range. Empty when no day is open.
 */
export function groupPickupHours(pickup: PickupInfo): HoursRow[] {
  const groups: { key: string; hours: string; days: DayKey[] }[] = []
  // Iterate the canonical list, never pickup.days as stored, so the output
  // order is stable whatever order the client sent.
  for (const { key } of DAYS) {
    if (!pickup.days?.includes(key)) continue
    const h = effectiveHours(pickup, key)
    const hours = h ? `${h.from} - ${h.to}` : ''
    let group = groups.find((g) => g.key === hours)
    if (!group) {
      group = { key: hours, hours, days: [] }
      groups.push(group)
    }
    group.days.push(key)
  }
  return groups.map((g) => ({ days: formatDayRange(g.days), hours: g.hours }))
}

/** Collapse runs of consecutive days: [mon,tue,wed,thu,sun] -> "Mon - Thu, Sun". */
export function formatDayRange(keys: DayKey[]): string {
  const order = DAYS.map((d) => d.key)
  const idx = keys.map((k) => order.indexOf(k)).sort((a, b) => a - b)
  const runs: { start: number; end: number }[] = []
  for (const i of idx) {
    const last = runs[runs.length - 1]
    if (last && i === last.end + 1) last.end = i
    else runs.push({ start: i, end: i })
  }
  return runs
    .map((r) => (r.start === r.end ? DAYS[r.start].short : `${DAYS[r.start].short} - ${DAYS[r.end].short}`))
    .join(', ')
}

/** "34300 Bagan Serai, Perak" from whichever parts are filled. */
export function pickupCityLine(pickup: PickupInfo): string {
  const town = [pickup.postcode, pickup.city].filter(Boolean).join(' ')
  return [town, pickup.state].filter(Boolean).join(', ')
}

/** Google Maps search for the pickup point, or null when there is nothing to search. */
export function pickupMapsUrl(pickup: PickupInfo): string | null {
  const query = [pickup.name, pickup.address, pickupCityLine(pickup)]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ')
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : null
}
